// /app/frontend/src/data/siteImages.js
//
// Central, branded-photo library for HomeGo Removals. The site-wide hero
// image, every service page, and the homepage all read from this file so
// any future photo refresh is a one-line change.
//
// Compression strategy: every photo is proxied through wsrv.nl which
// resizes + transcodes to WebP at q=70 on the fly. No build step, no API
// key, no CDN account. Saves 50-75% file size vs the original JPEGs.
// Falls back to the raw customer-asset URL on <img onError>.

const PROXY = 'https://wsrv.nl/';

/**
 * Build a wsrv.nl-proxied compressed URL.
 * @param {string} src   raw customer-asset URL
 * @param {number} w     pixel width (default 1200)
 * @param {number} q     WebP quality (default 70)
 */
export const compressImg = (src, w = 1200, q = 65) =>
  `${PROXY}?url=${encodeURIComponent(src)}&w=${w}&q=${q}&output=webp`;

/**
 * Build a responsive `srcSet` string for one source URL.
 * Mobile gets 640w, tablet 900w, desktop 1200w — Chrome / Safari /
 * Firefox all pick the right size from the `sizes` attribute.
 */
export const responsiveSrcSet = (src, q = 65) =>
  [
    `${compressImg(src, 640, q)} 640w`,
    `${compressImg(src, 900, q)} 900w`,
    `${compressImg(src, 1200, q)} 1200w`,
  ].join(', ');

// ---------------------------------------------------------------------------
// Raw (uncompressed) source URLs — single source of truth for the 5 newest
// branded customer-supplied photos. Stored as raw URLs so OG/Twitter/JSON-LD
// can reference canonical asset URLs that search engines prefer.
// ---------------------------------------------------------------------------
export const BRAND_PHOTOS = {
  // 1. Office relocation — two HomeGo crew in modern office, one pushing a
  // wrapped office cabinet on a trolley, other assembling a desk. HomeGo
  // branded van visible through window. "SERVER ROOM" / "DESK 1-4" crates.
  // BEST FOR: Office Relocation service page hero + commercial blog posts.
  officeRelocation: {
    raw: 'https://customer-assets.emergentagent.com/job_pack-move/artifacts/gs2pl74w_WhatsApp%20Image%202026-05-11%20at%2011.25.00.jpeg',
    alt: 'HomeGo Removals crew dismantling office furniture and moving wrapped equipment with branded HomeGo van outside',
    width: 1200, height: 1200,
  },

  // 2. London terraced houses — two HomeGo movers in front of branded
  // Luton van on Notting Hill-style colourful pastel terraced street,
  // lifting a large wrapped wardrobe.
  // BEST FOR: House Removals + Long-Distance Moves + London city pages.
  londonHouseMove: {
    raw: 'https://customer-assets.emergentagent.com/job_pack-move/artifacts/9q96p2kq_WhatsApp%20Image%202026-05-16%20at%2016.02.46.jpeg',
    alt: 'HomeGo Removals crew loading a wrapped wardrobe into a branded Luton van on a colourful Notting Hill terraced street, London',
    width: 1200, height: 1200,
  },

  // 3. Suburban detached house — three HomeGo crew with branded Luton
  // van outside UK brick detached home, unloading boxes (Oxfordshire
  // Council bin visible).
  // BEST FOR: Long-distance moves, Oxfordshire branch, family home moves,
  // homepage Trust section.
  familyHomeMove: {
    raw: 'https://customer-assets.emergentagent.com/job_pack-move/artifacts/izaao1cl_WhatsApp%20Image%202026-05-16%20at%2016.03.42.jpeg',
    alt: 'HomeGo Removals 3-man crew with branded Luton van unloading boxes at a UK brick detached family home',
    width: 1200, height: 1200,
  },

  // 4. Packing fragile crystal — HomeGo crew member wearing black
  // protective gloves carefully wrapping a crystal vase, "CHINA" labeled
  // HomeGo box, "FRAGILE" label visible.
  // BEST FOR: Packing Services page hero + fragile/antique blog posts.
  packingFragile: {
    raw: 'https://customer-assets.emergentagent.com/job_pack-move/artifacts/k6oqhdza_WhatsApp%20Image%202026-05-16%20at%2017.28.27.jpeg',
    alt: 'HomeGo Removals professional packer in protective gloves wrapping crystal glassware into a labelled FRAGILE / CHINA box',
    width: 1200, height: 1200,
  },

  // 5. HomeGo crew member with branded "HomeGo Removals & Packing" hoodie
  // loading a Luton van outside modern UK flats, branded HomeGo boxes
  // stacked outside, mover giving thumbs-up.
  // BEST FOR: Homepage hero + landing page lead image (highest brand impact).
  heroThumbsUp: {
    raw: 'https://customer-assets.emergentagent.com/job_pack-move/artifacts/d714jued_WhatsApp%20Image%202026-05-16%20at%2016.01.52.jpeg',
    alt: 'Smiling HomeGo Removals & Packing mover in branded hoodie giving a thumbs-up beside a Luton van and stacked HomeGo branded moving boxes',
    width: 1200, height: 1200,
  },

  // 6. Regional depot showcase — HomeGo Removals & Packing REGIONAL DEPOT
  // signage (green branding, phone 0330 043 4791, homego-removals.com URL)
  // with the full fleet parked out front: HomeGo-branded DAF 7-ton curtain-
  // side lorry (suitable for 4+ bedroom houses & long-distance moves) plus
  // two long-wheelbase Mercedes Maxi-mover Luton vans (suitable for 1-4
  // bedroom houses, short + long distance, domestic & commercial). Behind
  // the vehicles is the secure storage depot where customer moves are held
  // overnight. Every vehicle is fully insured and equipped with all
  // industry-standard removal kit.
  // BEST FOR: Homepage hero, fleet/trust proof section, About page, and
  // any Long-Distance / Commercial page hero. Highest single trust signal
  // on the site because it shows scale, real premises and real vehicles.
  regionalDepot: {
    raw: 'https://customer-assets-v7afamib.emergentagent.net/job_pack-move/artifacts/lf4eeypu_WhatsApp%20Image%202026-08-04%20at%2012.20.45.webp',
    alt: 'HomeGo Removals & Packing Regional Depot exterior showing the branded DAF 7-tonne removals lorry (4+ bedroom / long-distance capable) and long-wheelbase Mercedes Maxi-mover Luton vans (1-4 bedroom, domestic & commercial) parked in front of the secure overnight-storage depot — fully insured fleet, 0330 043 4791',
    width: 1200, height: 720,
  },

  // 7. HomeGo Renault Master Luton vans on a residential UK street — two
  // branded HomeGo Removals & Packing Master Luton vans (0330 043 4791,
  // "Your Move, Our Care") parked outside a modern brick apartment block.
  // BEST FOR: House Removals hero, block-of-flats / apartment moves,
  // London and city move pages, Move Cost Estimator hero.
  vansOnStreet: {
    raw: 'https://customer-assets-v7afamib.emergentagent.net/job_pack-move/artifacts/llsc59qp_WhatsApp%20Image%202026-08-04%20at%2012.43.24.jpeg',
    alt: 'Two HomeGo Removals & Packing Renault Master Luton vans branded "Your Move, Our Care" parked outside a modern UK residential apartment block — fully insured house and flat removals, 0330 043 4791',
    width: 1200, height: 720,
  },

  // 8. HomeGo movers wheeling a stack of packed cartons up a paved
  // residential path — branded HomeGo Removals & Packing hoodies (with
  // truck/box/car icons), stack of taped moving cartons on a sack-trolley.
  // BEST FOR: Packing Services hero, Furniture Assembly card, homepage
  // crew gallery, blog post authoring avatars.
  moversWithCartons: {
    raw: 'https://customer-assets-v7afamib.emergentagent.net/job_pack-move/artifacts/8rnnuv47_WhatsApp%20Image%202026-08-04%20at%2012.45.37.jpeg',
    alt: 'HomeGo Removals & Packing porters in branded hoodies wheeling a fully-wrapped stack of moving cartons up a UK apartment path — professional packing service delivering box-by-box on moving day',
    width: 1200, height: 1600,
  },

  // 9. Two HomeGo movers loading a Luton van at a modern UK apartment —
  // one carrying a wardrobe box towards an open van tailgate, other pulling
  // a suitcase trolley. Branded HomeGo hoodies + T-shirts.
  // BEST FOR: House Removals combo pages, block-of-flats specific pages,
  // Long-Distance Moves page.
  loadingApartmentVan: {
    raw: 'https://customer-assets-v7afamib.emergentagent.net/job_pack-move/artifacts/j7prqqhw_WhatsApp%20Image%202026-07-04%20at%2012.48.40.webp',
    alt: 'HomeGo Removals & Packing crew loading a Luton van at a modern UK apartment block — one mover carrying a wardrobe carton, another wheeling a suitcase trolley, all in branded HomeGo uniforms',
    width: 1200, height: 1600,
  },

  // 10. Alternate load-day angle — Same modern-apartment scene, wider crop
  // showing van cargo bay + landscaping. Higher trust signal because you
  // can see the van interior is clean and blanket-lined.
  // BEST FOR: Office Relocation hero, Storage Services card, apartment/
  // flat move blog posts.
  moveDayWideAngle: {
    raw: 'https://customer-assets-v7afamib.emergentagent.net/job_pack-move/artifacts/d8yw8jyd_WhatsApp%20Image%202026-78-04%20at%2012.48.40.webp',
    alt: 'HomeGo Removals & Packing crew mid-load at a UK residential apartment block — van cargo bay visible with clean blanketed interior, branded HomeGo uniforms, careful handling of customer goods',
    width: 1200, height: 1600,
  },

  // 11. Two HomeGo movers wheeling a tall stack of taped cartons up a
  // sunlit courtyard path — visible branded HomeGo hoodie logo (truck /
  // box / car icons) on the back. High-emotion trust shot: real crew,
  // real move-day, real care.
  // BEST FOR: Packing Services hero + Furniture Assembly + homepage
  // trust strip. Best "human element" photo we have.
  cartonStackDelivery: {
    raw: 'https://customer-assets-v7afamib.emergentagent.net/job_pack-move/artifacts/dl1vokan_WhatsApp%20Image%202026-08-04%20at%2012.48.40.webp',
    alt: 'HomeGo Removals & Packing crew wheeling a tall stack of professionally-packed cartons through a UK residential courtyard on moving day — branded HomeGo hoodies visible, full packing service in action',
    width: 1200, height: 1600,
  },
};

// ---------------------------------------------------------------------------
// Pre-built compressed image objects — paste this into any <img> or <figure>.
// Each entry has: src (compressed), srcSet (responsive), sizes, fallback (raw),
// alt, width, height.
// ---------------------------------------------------------------------------
const buildBundle = (key, sizesAttr) => {
  const photo = BRAND_PHOTOS[key];
  return {
    src: compressImg(photo.raw, 1200, 70),
    srcSet: responsiveSrcSet(photo.raw, 70),
    sizes: sizesAttr,
    fallback: photo.raw,
    alt: photo.alt,
    width: photo.width,
    height: photo.height,
  };
};

// Hero / above-the-fold sizing
const HERO_SIZES = '(max-width: 640px) 92vw, (max-width: 1024px) 60vw, 700px';
// Card / service-card sizing
const CARD_SIZES = '(max-width: 640px) 92vw, (max-width: 1024px) 45vw, 400px';

export const siteImages = {
  // Hero / landing — the highest-brand-impact shot
  homepageHero: buildBundle('heroThumbsUp', HERO_SIZES),
  // Service page heroes
  houseRemovalsHero: buildBundle('vansOnStreet', HERO_SIZES),
  officeRelocationHero: buildBundle('moveDayWideAngle', HERO_SIZES),
  packingServicesHero: buildBundle('cartonStackDelivery', HERO_SIZES),
  longDistanceHero: buildBundle('regionalDepot', HERO_SIZES),
  // Service card thumbnails
  houseRemovalsCard: buildBundle('vansOnStreet', CARD_SIZES),
  officeRelocationCard: buildBundle('loadingApartmentVan', CARD_SIZES),
  packingServicesCard: buildBundle('moversWithCartons', CARD_SIZES),
  longDistanceCard: buildBundle('regionalDepot', CARD_SIZES),
  brandHero: buildBundle('heroThumbsUp', CARD_SIZES),
};
